import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import _ from "lodash";
import { jwtDecode } from "jwt-decode";
import { useKeycloak } from "@react-keycloak/web";
import { RegionList } from "../graphql/queries/regionList";

export interface RegionOption {
    id: string;
    name: string;
}

/**
 * Region subscriptions are limited to global/region admins that can actually
 * see at least one region. The role gate avoids querying regionList for
 * everyone else (and stays safe should the backend not filter the list).
 */
const useRegionSubscriptionAccess = () => {
    const { keycloak } = useKeycloak();

    const isAdmin = useMemo(() => {
        if (!keycloak.token) return false;
        try {
            const decoded = jwtDecode<Record<string, unknown>>(keycloak.token);
            const roles = _.get(decoded, "otto_access.roles", []) as string[];
            return roles.includes("globalAdmin") || roles.includes("regionAdmin");
        } catch {
            return false;
        }
    }, [keycloak.token]);

    const { data, loading } = useQuery(RegionList, { skip: !isAdmin });

    const regions: RegionOption[] = useMemo(() => {
        const list = (data?.regionList?.regions ?? []) as any[];
        return list
            .filter((r) => !!r?.id)
            .map((r) => ({ id: String(r.id), name: r.name ?? "" }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [data]);

    return {
        showRegionUi: isAdmin && regions.length > 0,
        regions,
        loading: isAdmin && loading,
    };
};

export default useRegionSubscriptionAccess;
